/**
 * SIGN-IN. The POC's login screen (`core/auth/login/`, `main` @ b9c1b73), on
 * this repo's stack and against PharmOS API 1.0.0.
 *
 * THE SHAPE IS THE POC's: a full screen split in two — what the platform IS on
 * the left, the step you are on to the right of it, a footer under both. The
 * left half names the company once the right half has answered which one, and
 * nothing before that.
 *
 * THREE STEPS, in the POC's order:
 *   1. credentials   company id · staff id · password   → POST /v1/user/login
 *   2. newPassword   only when the server says the credential is spent
 *                    → POST /v1/user/login/reset-password
 *   3. context       which shop you are standing in
 *                    → POST /v1/user/login/branches/{branchCode}
 *
 * THE ONE-TIME PASSWORD'S STEP SITS BETWEEN THE OTHER TWO, not after them: the
 * number has just been typed and nothing about a branch has been asked yet.
 *
 * WHAT IS NOT DRAWN, AND WHY: the system-status strip, the shift-handover banner
 * and the counter declaration. Each needs a fact PharmOS API 1.0.0 does not
 * state, and a sign-in screen is the wrong place to invent one. They are listed
 * in `docs/TODO.md`.
 *
 * A REFUSAL IS THE SERVER'S SENTENCE. The backend returns the code and the
 * message; this screen prints the message and does not keep a wording of its
 * own (`docs/DECISIONS.md`, 24-09-2026). It also does not count failures: the
 * server locks the account at five.
 */
import { useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { z } from "zod";
import { errorText } from "@/components/feedback/error-text";
import { isAppError } from "@/api/problems";
import { readToken, writeToken } from "@/api/token";
import {
  ContextPanel,
  CredentialsPanel,
  LoginFooter,
  NewPasswordPanel,
  PlatformPanel,
} from "@/features/auth";
import { PASSWORD_MIN_LENGTH, PIN_LENGTH, credentialPolicyOk } from "@/domain/credential-policy";
import { localName, otherLocalName } from "@/domain/local-name";
import { applyAccountLanguage, useT } from "@/i18n";
import { chooseBranch, login, resetExpiredPassword, type LoginPayload } from "@/lib/auth/api";
import {
  buildSession,
  currentSessionEpoch,
  dropOperatorQueries,
  endOperatorSession,
  saveSession,
  sessionKeys,
  sessionQuery,
} from "@/lib/auth/session";

/** Only a path on this site may be a redirect target: never another origin. */
export function safeRedirect(value: string | undefined): string {
  return value && value.startsWith("/") && !value.startsWith("//") ? value : "/";
}

export const Route = createFileRoute("/login")({
  validateSearch: z.object({ redirect: z.string().optional() }),
  beforeLoad: async ({ context, search }) => {
    // A transient profile-read failure (the network down, a 5xx) must not
    // dead-end a signed-in operator on the route's error state with no retry:
    // fall through to the sign-in form. The session stays the server's to
    // decide — the next real read answers 401 and the guard does its work.
    const session = await context.queryClient.query(sessionQuery).catch(() => null);
    if (session) throw redirect({ href: safeRedirect(search.redirect) });
  },
  component: LoginPage,
});

type Step = "credentials" | "newPassword" | "context";

/** The interim token was rejected. The operator goes back to the first step. */
function interimRejected(error: unknown): boolean {
  return isAppError(error) && error.kind === "unauthenticated";
}

function LoginPage() {
  const { t } = useT();
  const { redirect: target } = Route.useSearch();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>("credentials");
  const [companyId, setCompanyId] = useState("");
  const [staffId, setStaffId] = useState("");
  const [password, setPassword] = useState("");
  const [payload, setPayload] = useState<LoginPayload | null>(null);
  const [storeId, setStoreId] = useState<string | null>(null);
  const [policyError, setPolicyError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  /**
   * A branch exchange that timed out may have been applied. The contract does
   * not say a repeat is safe, so the step offers a restart and does not send
   * the request again (`GAPS.md` E).
   */
  const [branchUncertain, setBranchUncertain] = useState(false);
  /** One submission at a time. `isPending` is still false until the next render. */
  const flight = useRef(false);

  const branches = payload?.branches ?? [];

  /** Takes a payload from step 1 or step 2 and moves to whatever it calls for. */
  function accept(next: LoginPayload) {
    writeToken(next.authToken, "interim");
    applyAccountLanguage(next.displayLang);
    setPayload(next);
    // A company with one branch: preselected, because a list of one is a
    // statement rather than a choice. The press still belongs to the operator.
    const only = next.branches?.length === 1 ? next.branches[0]?.branchCode : undefined;
    setStoreId(only ?? null);
    setStep(next.isPasswordExpired === true ? "newPassword" : "context");
  }

  const signIn = useMutation({
    mutationFn: () => {
      // A fresh sign-in never carries the last operator's token or their rows.
      // No notify: this screen is sign-in, and invalidating it would remount the form.
      endOperatorSession(queryClient, { notify: false });
      return login({ tenantId: companyId.trim(), staffId: staffId.trim(), password });
    },
    onSuccess: accept,
    onSettled: () => {
      flight.current = false;
    },
  });

  const replacePassword = useMutation({
    mutationFn: (next: string) => resetExpiredPassword(next),
    onSuccess: (next, submitted) => {
      // The new password becomes the one this sign-in uses.
      setPassword(submitted);
      accept(next);
    },
    onError: (error) => {
      if (interimRejected(error)) resetSignIn(t("auth.signInExpired"));
    },
    onSettled: () => {
      flight.current = false;
    },
  });

  const enterBranch = useMutation({
    mutationFn: async (branchCode: string) => {
      // The context step renders only while a payload is held, so an absent
      // one means the sign-in was restarted under this press — the same case
      // the epoch check below covers for the request itself. Return, as that
      // check does, rather than throwing on a state the screen cannot reach.
      const current = payload;
      if (!current) return null;
      const epoch = currentSessionEpoch();
      const chosen = await chooseBranch(branchCode);
      // Signed out while the exchange was in flight: do not write the token back.
      if (epoch !== currentSessionEpoch()) return null;
      writeToken(chosen.authToken, "active");
      return buildSession(current, branchCode, companyId.trim(), {
        company: chosen.company,
        branch: chosen.branch,
      });
    },
    onSuccess: async (session) => {
      if (!session || readToken()?.stage !== "active") return;
      dropOperatorQueries(queryClient);
      saveSession(session);
      queryClient.setQueryData(sessionKeys.current(), session);
      await navigate({ href: safeRedirect(target) });
    },
    onError: (error) => {
      // The interim token stays for a failure the operator can repeat: the
      // network, an answered refusal, a 5xx. Clearing it made the retry a
      // request the server could not authorise.
      if (isAppError(error) && error.kind === "timeout") {
        setBranchUncertain(true);
        return;
      }
      if (interimRejected(error)) resetSignIn(t("auth.signInExpired"));
    },
    onSettled: () => {
      flight.current = false;
    },
  });

  /** Back to the first step, with no sentence left over from the one just left. */
  function resetSignIn(nextNotice: string | null) {
    endOperatorSession(queryClient, { notify: false });
    setPayload(null);
    setStoreId(null);
    setPassword("");
    setPolicyError(null);
    setNotice(nextNotice);
    setBranchUncertain(false);
    signIn.reset();
    replacePassword.reset();
    enterBranch.reset();
    flight.current = false;
    setStep("credentials");
  }

  function runOnce(pending: boolean, run: () => void) {
    if (flight.current || pending) return;
    flight.current = true;
    run();
  }

  function submitCredentials() {
    // NAMES BOTH SHAPES. A message about password length in front of somebody
    // holding six digits sends them to look for a credential never issued.
    if (!credentialPolicyOk(password)) {
      setPolicyError(t("auth.credentialShape", { min: PASSWORD_MIN_LENGTH, pin: PIN_LENGTH }));
      return;
    }
    setPolicyError(null);
    setNotice(null);
    runOnce(signIn.isPending, () => {
      signIn.mutate();
    });
  }

  const company = payload?.company;
  const displayLang = payload?.displayLang;
  const companyName =
    localName(company?.companyNameEn, company?.companyNameZh, displayLang) || companyId.trim();
  const companyNameAlt = otherLocalName(company?.companyNameEn, company?.companyNameZh, displayLang) || null;

  return (
    <div className="flex min-h-dvh flex-col bg-bg">
      <main className="grid min-h-0 flex-1 bg-surface lg:grid-cols-2">
        <PlatformPanel
          {...(payload
            ? {
                companyName,
                companyNameAlt,
                companyId: companyId.trim(),
                branchCount: branches.length,
              }
            : {})}
        />

        {step === "credentials" && (
          <CredentialsPanel
            companyId={companyId}
            staffId={staffId}
            password={password}
            onCompanyId={setCompanyId}
            onStaffId={setStaffId}
            onPassword={(value) => {
              setPolicyError(null);
              setPassword(value);
            }}
            onSubmit={submitCredentials}
            signingIn={signIn.isPending}
            error={policyError ?? notice ?? (signIn.error ? errorText(signIn.error) : null)}
          />
        )}

        {step === "newPassword" && payload && (
          <NewPasswordPanel
            staffId={payload.staffId}
            busy={replacePassword.isPending}
            error={replacePassword.error ? errorText(replacePassword.error) : null}
            onSubmit={(next) => {
              runOnce(replacePassword.isPending, () => {
                replacePassword.mutate(next);
              });
            }}
            onBack={() => resetSignIn(null)}
          />
        )}

        {step === "context" && payload && (
          <ContextPanel
            staffName={
              localName(payload.staffNameEn, payload.staffNameZh, payload.displayLang) || payload.staffId
            }
            staffId={payload.staffId}
            companyName={companyName}
            companyNameAlt={companyNameAlt}
            companyId={companyId.trim()}
            branches={branches}
            storeId={storeId}
            onStoreId={setStoreId}
            displayLang={payload.displayLang}
            signingIn={enterBranch.isPending}
            mustRestart={branchUncertain}
            error={
              branchUncertain
                ? t("auth.branchExchangeUncertain")
                : enterBranch.error
                  ? errorText(enterBranch.error)
                  : null
            }
            onBack={() => resetSignIn(null)}
            onSubmit={() => {
              if (!storeId || branchUncertain) return;
              runOnce(enterBranch.isPending, () => {
                enterBranch.mutate(storeId);
              });
            }}
          />
        )}
      </main>
      <LoginFooter />
    </div>
  );
}
